/**
 * AdPreview.jsx
 * The advert as the feed will render it.
 *
 * A preview rather than a validation message, because "the headline is too
 * long" is a sentence you have to decode, and a headline visibly running out
 * of room is not.
 *
 * A paired advert draws two cards, one per platform, and that is the point:
 * the whole reason a pair exists is that the two posts say different things.
 * Showing one card and a note would hide exactly the difference the user is
 * about to pay to run.
 *
 * Dependencies: components/MediaPreview.jsx
 */

import MediaPreview from '../MediaPreview.jsx';

const NAME = { facebook: 'Facebook', instagram: 'Instagram' };

export default function AdPreview({
  brandName,
  source,
  posts,
  media,
  headline,
  body,
  description,
  link,
  ctaLabel,
  placements,
}) {
  if (source === 'post') {
    if (posts.length === 0) {
      return (
        <Card brandName={brandName} where="Nothing chosen yet">
          <p className="ad-preview__body">Choose a post and it appears here.</p>
          <div className="ad-preview__image ad-preview__image--empty">No post chosen</div>
        </Card>
      );
    }

    return (
      <>
        {posts.map((p) => (
          <div key={p.platform} style={{ marginBottom: posts.length > 1 ? 12 : 0 }}>
            <Card brandName={brandName} where={NAME[p.platform]}>
              <p className="ad-preview__body">{p.caption?.trim() || 'This post has no caption.'}</p>

              {p.imageUrl ? (
                <img src={p.imageUrl} alt="" className="ad-preview__image" />
              ) : (
                <div className="ad-preview__image ad-preview__image--empty">
                  This post has no picture
                </div>
              )}

              {/* No link means no footer at all, which is exactly how it runs. */}
              {link.trim() && (
                <Foot link={link} ctaLabel={ctaLabel}>
                  {null}
                </Foot>
              )}
            </Card>
          </div>
        ))}
      </>
    );
  }

  return (
    <Card brandName={brandName} where={whereLabel(placements)}>
      <p className="ad-preview__body">{body || 'Your body text appears here.'}</p>

      {media[0] ? (
        <MediaPreview media={media[0]} className="ad-preview__image" />
      ) : (
        <div className="ad-preview__image ad-preview__image--empty">No image chosen</div>
      )}

      <Foot link={link} ctaLabel={ctaLabel}>
        <>
          <div className="ad-preview__headline">{headline || 'Your headline'}</div>
          {description && <div className="ad-preview__desc">{description}</div>}
        </>
      </Foot>
    </Card>
  );
}

function Card({ brandName, where, children }) {
  return (
    <div className="ad-preview">
      <div className="ad-preview__head">
        <span className="avatar">{(brandName ?? '?').slice(0, 1)}</span>
        <div>
          <div className="ad-preview__brand">{brandName}</div>
          <div className="ad-preview__sponsored">Sponsored · {where}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

function Foot({ link, ctaLabel, children }) {
  return (
    <div className="ad-preview__foot">
      <div style={{ minWidth: 0 }}>
        <div className="ad-preview__domain">
          {link.replace(/^https?:\/\//, '').split('/')[0] || 'your-site.com'}
        </div>
        {children}
      </div>
      <span className="ad-preview__cta">{ctaLabel}</span>
    </div>
  );
}

function whereLabel(placements) {
  if (placements.length === 0) return 'Nowhere yet';
  if (placements.length === 2) return 'Facebook and Instagram';
  return NAME[placements[0]];
}
